import { JSONParse, isRecord } from '@utils/json'

import { getHandledMessageStore, type HandledMessageStore } from './messageStore'
import type { WebSocketMessage, WebSocketMessageHandler, WebSocketMessageMatcher } from './types'

type WebSocketMessageListener = {
  handler: WebSocketMessageHandler
  matcher: WebSocketMessageMatcher
}

type RouteMessageOptions = { dedupe?: boolean }

type MessageRouterOptions = {
  messageStore?: HandledMessageStore
  messageStoresByType?: Readonly<Record<string, HandledMessageStore>>
}

type RouteMessageResult = {
  duplicate: boolean
  message: WebSocketMessage | null
}

const matchesMessageType = (message: WebSocketMessage, type: string): boolean =>
  message.type === type || message.$type === type

const readMessage = (value: unknown): WebSocketMessage | null => {
  if (typeof value === 'string') {
    const parsedMessage = JSONParse<unknown>(value)
    return isRecord(parsedMessage) ? parsedMessage : null
  }

  return isRecord(value) ? value : null
}

const matchesMessage = (matcher: WebSocketMessageMatcher, message: WebSocketMessage): boolean => {
  if (typeof matcher === 'string') return matchesMessageType(message, matcher)
  return matcher(message)
}

const safelyNotifyListener = (
  listener: WebSocketMessageListener,
  message: WebSocketMessage
): void => {
  try {
    if (!matchesMessage(listener.matcher, message)) return

    listener.handler(message)
  } catch (error) {
    console.error('[WebSocket] Message listener failed:', error)
  }
}

export const createWebSocketMessageRouter = ({
  messageStore = getHandledMessageStore(),
  messageStoresByType = {},
}: MessageRouterOptions = {}) => {
  const listeners = new Set<WebSocketMessageListener>()
  const storesByType = new Map(Object.entries(messageStoresByType))

  const routeMessage = (
    rawMessage: unknown,
    options: RouteMessageOptions = {}
  ): RouteMessageResult => {
    const message = readMessage(rawMessage)

    if (!message) return { duplicate: false, message: null }

    const shouldDedupe = options.dedupe ?? true
    const store =
      (typeof message.type === 'string' ? storesByType.get(message.type) : undefined) ??
      (typeof message.$type === 'string' ? storesByType.get(message.$type) : undefined) ??
      messageStore
    if (shouldDedupe && !store.rememberMessage(message)) {
      return { duplicate: true, message }
    }

    listeners.forEach((listener) => {
      safelyNotifyListener(listener, message)
    })

    return { duplicate: false, message }
  }

  const subscribe = (
    matcher: WebSocketMessageMatcher,
    handler: WebSocketMessageHandler
  ): (() => void) => {
    const listener = { matcher, handler }
    listeners.add(listener)

    return () => {
      listeners.delete(listener)
    }
  }

  return { routeMessage, subscribe }
}
